/**
 * RailNav — the navigation group at the very top of the left rail plus the
 * Projects section header (label + "+" new-project button).
 *
 * Shared by NewChat, DesignView, and AdminUsage so the rail reads the same
 * everywhere: nav items first, everything project-related below
 * (per the approved usage-analytics design).
 *
 * The Usage analytics item is visible to admins only (config.adminGroups /
 * config.adminEmails vs the signed-in user); the server's ADMIN_GROUPS /
 * ADMIN_EMAILS check on /admin/usage stays the real gate.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getConfig } from '../services/config';

export function isUsageAdmin(user) {
  const cfg = getConfig();
  const groups = user?.groups || [];
  const email = (user?.email || '').toLowerCase();
  const adminGroups = cfg.adminGroups || [];
  const adminEmails = (cfg.adminEmails || []).map((e) => String(e).toLowerCase());
  return groups.some((g) => adminGroups.includes(g)) || (email && adminEmails.includes(email));
}

export default function RailNav({ active = '', onNewProject, search = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isUsageAdmin(user);

  // ⌘K / Ctrl+K focuses the rail search on every page that renders one.
  // Dep on the boolean, not the element — `search` is fresh JSX every render.
  const hasSearch = !!search;
  React.useEffect(() => {
    if (!hasSearch) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const input = document.querySelector('.leftrail-search input');
        if (input) {
          input.focus();
          input.select();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hasSearch]);

  return (
    <>
      <nav className="rail-nav" aria-label="Main">
        <button
          type="button"
          className={`rail-nav-item${active === 'home' ? ' active' : ''}`}
          onClick={() => navigate('/')}
        >
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M2 8.5 8 3l6 5.5V14h-4v-4H6v4H2V8.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
          Home
        </button>
        {admin && (
          <button
            type="button"
            className={`rail-nav-item${active === 'usage' ? ' active' : ''}`}
            onClick={() => navigate('/admin/usage')}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3 13V9m5 4V5m5 8V3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            Usage analytics
            <span className="admin-pill">admin</span>
          </button>
        )}
      </nav>
      {/* Search pill sits between the nav group and the PROJECTS header —
          matches the approved rail mock. Pages own the query state and pass
          their pill in so ⌘K handlers keep working unchanged. */}
      {search}
      <div className="rail-section-head">
        <span className="label">Projects</span>
        {onNewProject && (
          <button type="button" className="add" title="New project" aria-label="New project" onClick={onNewProject}>
            +
          </button>
        )}
      </div>
    </>
  );
}
